import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { aiModelsApi } from '@/api/endpoints'
import { toast } from '@/lib/toast'

/**
 * Superuser species-catalog management for the open-vocabulary (speciesnet-bioclip)
 * model (#108). Lists, adds and removes DetectionClass rows for one base model.
 */
export function useSpeciesCatalog(baseModelId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation('detectionAccuracy')
  const key = ['species-catalog', baseModelId]
  const query = useQuery({
    queryKey: key,
    queryFn: () => aiModelsApi.getDetectionClasses(baseModelId),
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: key })

  const add = useMutation({
    mutationFn: (className: string) =>
      aiModelsApi.createDetectionClass({ base_model: baseModelId, class_name: className }),
    onSuccess: (dc) => { toast.success(t('catalog.added', { name: dc.class_name })); invalidate() },
    onError: () => toast.error(t('catalog.addFailed')),
  })
  const remove = useMutation({
    mutationFn: (id: number) => aiModelsApi.deleteDetectionClass(id),
    onSuccess: () => { toast.success(t('catalog.removed')); invalidate() },
    onError: () => toast.error(t('catalog.removeFailed')),
  })

  return {
    species: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    add: add.mutate,
    adding: add.isPending,
    remove: remove.mutate,
    removing: remove.isPending,
  }
}
